/**
 * Base API Client
 * Provides shared functionality for all Route Royal API clients
 */

import { getConfig } from '../config/env';
import { apiCache } from './cache';
import { etagStore } from './etag-store';
import { getServiceAuthHeader } from './service-token';

const ROUTE_ROYAL_API_URL = getConfig().api.routeRoyal;

/**
 * Generate a correlation ID for request tracing
 */
function generateCorrelationId(): string {
  const prefix = process.env.ROYAL_CORRELATION_PREFIX || 'royale';
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  return `${prefix}-${timestamp}-${random}`;
}

export interface RequestResult<T> {
  data: T;
  notModified?: boolean;
  etag?: string | undefined;
}

export class BaseAPIClient {
  protected baseUrl: string;
  protected apiVersion: string = '/api/v1';
  protected baseUrlIncludesVersion: boolean = false;
  
  constructor(baseUrl: string = ROUTE_ROYAL_API_URL) {
    // Normalize the base URL - remove trailing slash
    this.baseUrl = baseUrl.replace(/\/+$/, '');
    
    // Check if base URL already includes API version (e.g., http://localhost:3001/api/v1)
    // This handles cases where ROUTE_ROYAL_API_URL includes the version path
    this.baseUrlIncludesVersion = this.baseUrl.endsWith('/api/v1') || 
                                   this.baseUrl.includes('/api/v1/');
    
    // Log once for debugging URL configuration (development only)
    if (typeof globalThis !== 'undefined' && !(globalThis as unknown as Record<string, boolean>).__apiClientUrlLogged) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console -- Development debugging
        console.log('[API] Base URL configuration:', {
          baseUrl: this.baseUrl,
          includesVersion: this.baseUrlIncludesVersion,
        });
      }
      (globalThis as unknown as Record<string, boolean>).__apiClientUrlLogged = true;
    }
  }
  
  /**
   * Normalise endpoint by adding API version prefix
   * Skips prefix for endpoints that are excluded from versioning (like /oauth/token)
   * Also skips prefix if baseUrl already includes the version
   */
  protected normaliseEndpoint(endpoint: string): string {
    // List of endpoints excluded from API versioning (must match API's globalPrefix exclusions)
    const excludedPaths = ['/oauth/token', '/metrics'];
    
    // If endpoint is in excluded list, return as-is
    if (excludedPaths.some(path => endpoint.startsWith(path))) {
      return endpoint;
    }
    
    // If endpoint already has /api/v1, don't add it again
    if (endpoint.startsWith('/api/v1')) {
      return endpoint;
    }
    
    // If base URL already includes version, don't add it again
    if (this.baseUrlIncludesVersion) {
      return endpoint;
    }
    
    // Add API version prefix
    return `${this.apiVersion}${endpoint}`;
  }
  
  /**
   * Make an API request with caching support
   * Gracefully handles network errors and API unavailability
   */
  protected async request<T>(
    endpoint: string,
    options?: RequestInit,
    useCache: boolean = true
  ): Promise<T> {
    // Generate cache key
    const cacheKey = `${this.baseUrl}${endpoint}:${JSON.stringify(options?.body || {})}`;
    
    // Check cache for GET requests
    if (useCache && (!options?.method || options.method === 'GET')) {
      const cached = apiCache.get<T>(cacheKey);
      if (cached) {
        const DEBUG_CACHE = process.env.DEBUG_API === 'true';
        if (DEBUG_CACHE) {
          // eslint-disable-next-line no-console
          console.log('[API] Returning cached response:', {
            endpoint,
            cachedKeys: cached && typeof cached === 'object' ? Object.keys(cached as object) : 'not-object',
          });
        }
        return cached;
      }
    }
    
    try {
      // Create abort controller for timeout (compatible with all Node versions)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Correlation-Id': generateCorrelationId(),
        ...options?.headers as Record<string, string>,
      };

      // Only add ngrok header in development - it's required for ngrok free tier
      // but causes CORS failures in production (header not in Route Royal's allowedHeaders)
      if (process.env.NODE_ENV === 'development') {
        headers['ngrok-skip-browser-warning'] = 'true';
      }

      if (!headers.Authorization) {
        const authHeader = await getServiceAuthHeader();
        if (authHeader) {
          // Validate auth header before using it
          if (authHeader.length < 20) {
            console.error('[API] Authorization header appears truncated!', {
              headerLength: authHeader.length,
              headerPreview: authHeader.substring(0, 30),
            });
            throw new Error(`Invalid authorization header (length: ${authHeader.length})`);
          }
          headers.Authorization = authHeader;
        }
      }

      const normalisedEndpoint = this.normaliseEndpoint(endpoint);
      const response = await fetch(`${this.baseUrl}${normalisedEndpoint}`, {
        ...options,
        headers,
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        // Parse error response for detailed logging
        let errorBody: { message?: string | string[] | unknown; error?: string; statusCode?: number } = {};
        try {
          errorBody = await response.json();
        } catch {
          errorBody = { message: response.statusText };
        }
        
        // Extract error message - handle string, array, or object
        const extractMessage = (msg: unknown): string => {
          if (typeof msg === 'string') return msg;
          if (Array.isArray(msg)) return msg.join(', ');
          if (msg && typeof msg === 'object') return JSON.stringify(msg);
          return 'unknown';
        };
        
        const messageStr = extractMessage(errorBody.message || errorBody.error);
        
        // Log error with flattened details for better visibility in dev tools
        // Only log 5xx as errors, 4xx are expected in many cases (404 for missing resources, etc.)
        const errorDetails = `url=${this.baseUrl}${normalisedEndpoint} status=${response.status} statusText=${response.statusText} message=${messageStr}`;
        if (response.status >= 500) {
          console.error(`[API] Server error: ${errorDetails}`);
        } else if (process.env.DEBUG_API === 'true') {
          // Only log 4xx errors when DEBUG_API is enabled to reduce noise
          console.warn(`[API] Request failed: ${errorDetails}`);
        }
        
        // Include status code in error message for better debugging
        throw new Error(`${messageStr} (HTTP ${response.status})`);
      }
      
      // Handle empty responses gracefully
      const responseText = await response.text();
      if (!responseText || responseText.trim() === '') {
        // Return empty object for empty responses (but don't cache it)
        console.warn('[API] Empty response body received:', {
          url: `${this.baseUrl}${normalisedEndpoint}`,
          status: response.status,
        });
        return {} as T;
      }
      
      let responseData: unknown;
      try {
        responseData = JSON.parse(responseText);
      } catch (parseError) {
        console.error('[API] Failed to parse JSON response:', {
          url: `${this.baseUrl}${normalisedEndpoint}`,
          responseText: responseText.substring(0, 200),
          error: parseError,
        });
        throw new Error(`Invalid JSON response from API`);
      }
      
      // Unwrap API response envelope if present
      // API may return: { success: true, data: {...} } or just { data: {...} }
      // We want to return just the inner data
      let data: T;
      const DEBUG_API = process.env.DEBUG_API === 'true';
      
      // Check if response is wrapped in an envelope (has 'data' key at top level)
      // Handles both { success: true, data: {...} } and { data: {...} }
      const hasDataEnvelope = responseData && 
        typeof responseData === 'object' && 
        'data' in responseData &&
        // Only unwrap if 'data' is the primary content (not a legitimate field like listings.data)
        // Check if there are only envelope keys (success, data, message, error, meta, etc.)
        Object.keys(responseData as object).every(key => 
          ['success', 'data', 'message', 'error', 'meta', 'pagination', 'statusCode'].includes(key)
        );
      
      if (hasDataEnvelope) {
        data = (responseData as { data: T }).data;
        if (DEBUG_API) {
          // eslint-disable-next-line no-console
          console.log('[API] Unwrapped envelope response:', {
            endpoint: normalisedEndpoint,
            envelopeKeys: Object.keys(responseData as object),
            dataKeys: data && typeof data === 'object' ? Object.keys(data) : 'not-object',
            dataPreview: JSON.stringify(data).substring(0, 200),
          });
        }
      } else {
        data = responseData as T;
        if (DEBUG_API) {
          // eslint-disable-next-line no-console
          console.log('[API] Direct response (no envelope):', {
            endpoint: normalisedEndpoint,
            dataKeys: data && typeof data === 'object' ? Object.keys(data) : 'not-object',
          });
        }
      }
      
      // Cache successful GET responses (only if data is not empty)
      const isEmptyObject = data && typeof data === 'object' && Object.keys(data).length === 0;
      if (useCache && (!options?.method || options.method === 'GET') && !isEmptyObject) {
        apiCache.set(cacheKey, data, 60 * 1000); // Cache for 1 minute
      } else if (isEmptyObject) {
        console.warn('[API] Not caching empty object response:', { endpoint });
      }
      
      return data;
    } catch (error) {
      // Handle network errors (API server not available)
      const isNetworkError = 
        error instanceof TypeError ||
        (error instanceof Error && (
          error.message.includes('fetch') ||
          error.message.includes('Failed to fetch') ||
          error.message.includes('NetworkError') ||
          error.name === 'AbortError'
        ));
      
      if (isNetworkError) {
        const isDevelopment = process.env.NODE_ENV === 'development';
        if (isDevelopment) {
          console.warn(
            `[API] Route Royal server not available at ${this.baseUrl}${endpoint}. ` +
            `This is expected in development when the server isn't running. ` +
            `The app will use mock data instead.`
          );
        }
        // Re-throw to let calling code handle fallback
        throw new Error(`API server unavailable: ${error instanceof Error ? error.message : 'Network error'}`);
      }
      // Re-throw other errors
      throw error;
    }
  }
  
  /**
   * Make an API request with ETag support for conditional GET requests
   * Returns both data and metadata (ETag, notModified flag)
   */
  protected async requestWithETag<T>(
    endpoint: string,
    options?: RequestInit,
    useCache: boolean = true
  ): Promise<RequestResult<T>> {
    const cacheKey = `${this.baseUrl}${endpoint}:${JSON.stringify(options?.body || {})}`;
    const method = options?.method || 'GET';
    
    // Only use ETag for GET requests
    if (method !== 'GET') {
      const data = await this.request<T>(endpoint, options, useCache);
      return { data };
    }
    
    try {
      // Build headers with correlation ID
      const headersRecord: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Correlation-Id': generateCorrelationId(),
      };
      
      // Add existing headers from options
      if (options?.headers) {
        const existingHeaders = options.headers as Record<string, string>;
        Object.assign(headersRecord, existingHeaders);
      }

      if (!headersRecord.Authorization) {
        const authHeader = await getServiceAuthHeader();
        if (authHeader) {
          headersRecord.Authorization = authHeader;
        }
      }
      
      // Add If-None-Match header if we have a previous ETag
      const previousETag = etagStore.get(cacheKey);
      if (previousETag) {
        headersRecord['If-None-Match'] = previousETag;
      }
      
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      const normalisedEndpoint = this.normaliseEndpoint(endpoint);
      const response = await fetch(`${this.baseUrl}${normalisedEndpoint}`, {
        ...options,
        method,
        headers: headersRecord,
        signal: controller.signal,
        cache: 'no-store', // Disable fetch cache to use ETag properly
      });
      
      clearTimeout(timeoutId);
      
      // Handle 304 Not Modified
      if (response.status === 304) {
        // Return cached data if available
        const cached = apiCache.get<T>(cacheKey);
        if (cached) {
          return {
            data: cached,
            notModified: true,
            etag: previousETag,
          };
        }
        // If cache was cleared but we got 304, fetch fresh data
        // (Remove the If-None-Match header and retry)
        etagStore.delete(cacheKey);
        return this.requestWithETag<T>(endpoint, options, useCache);
      }
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(error.message || 'API request failed');
      }
      
      // Get ETag from response
      const etag = response.headers.get('ETag') || undefined;
      
      // Parse response data
      const responseData = await response.json();
      
      // Unwrap API response envelope if present
      // API may return: { success: true, data: {...} } or just { data: {...} }
      // We want to return just the inner data
      let data: T;
      const hasDataEnvelope = responseData && 
        typeof responseData === 'object' && 
        'data' in responseData &&
        Object.keys(responseData as object).every(key => 
          ['success', 'data', 'message', 'error', 'meta', 'pagination', 'statusCode'].includes(key)
        );
      
      if (hasDataEnvelope) {
        data = responseData.data as T;
      } else {
        data = responseData as T;
      }
      
      // Store ETag for future requests
      if (etag) {
        etagStore.set(cacheKey, etag);
      }
      
      // Cache the response
      if (useCache) {
        apiCache.set(cacheKey, data, 60 * 1000); // Cache for 1 minute
      }
      
      return {
        data,
        notModified: false,
        etag,
      };
    } catch (error) {
      // Handle network errors
      const isNetworkError = 
        error instanceof TypeError ||
        (error instanceof Error && (
          error.message.includes('fetch') ||
          error.message.includes('Failed to fetch') ||
          error.message.includes('NetworkError') ||
          error.name === 'AbortError'
        ));
      
      if (isNetworkError) {
        const isDevelopment = process.env.NODE_ENV === 'development';
        if (isDevelopment) {
          console.warn(
            `[API] Route Royal server not available at ${this.baseUrl}${endpoint}. ` +
            `This is expected in development when the server isn't running.`
          );
        }
        throw new Error(`API server unavailable: ${error instanceof Error ? error.message : 'Network error'}`);
      }
      throw error;
    }
  }
}

